import classNames from 'classnames';
import { useRef, useState, PropsWithChildren, useEffect, MouseEventHandler } from 'react';

import { slideUp, slideDown } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import Chevron from '~images/inline/triangle-right.inline.svg';

interface IProps {
    question?: string;
    answer?: string;
    className?: string;
    id?: string;
    forceopen?: boolean;
}

const Question = ({
    question = '',
    answer = '',
    id = '',
    className = '',
    forceopen = false,
}: PropsWithChildren<IProps>) => {
    const [isOpen, setIsOpen] = useState(false);
    const contentRef = useRef(null as HTMLDivElement | null);
    id = id || `faqItem-${Math.random().toString(36).substring(2, 9)}`;
    const trackingComponentName = 'FAQ';
    const trackFaqItemClicked = (id?: string) => {
        fireTrackEvent({ page: { action: `${trackingComponentName}: Accordion expand: ${id}` } });
    };

    useEffect(() => {
        forceopen && setIsOpen(forceopen);
    }, [forceopen]);

    const toggleContent = (): MouseEventHandler<HTMLButtonElement> | undefined => {
        if (!contentRef.current) {
            return;
        }
        isOpen ? slideUp(contentRef.current) : slideDown(contentRef.current);
        setIsOpen(!isOpen);
        if (!isOpen) trackFaqItemClicked(contentRef.current.id);
    };

    const editedId = id.toLowerCase().replace(/[^a-z0-9]/gi, '');

    const styledAnswer = answer.includes('<a') ? answer.replace('<a', '<a class="underline break-all"') : answer;

    return (
        <li
            className={`shadow-md rounded-lg bg-white mb-8 p-6 ${className || ''}`}
            data-qa="faqItem-row"
        >
            <button
                className="w-full flex items-center justify-between text-left"
                onClick={toggleContent}
                aria-expanded={isOpen}
                id={`btn-${editedId}`}
                aria-controls={`content-${editedId}`}
                data-qa="faqItem-row"
            >
                <p className="font-bold text-14 lg:text-18 font-poppins leading-24">{question}</p>
                <Chevron
                    aria-hidden="true"
                    data-qa="faqItem-arrow"
                    className={classNames(
                        'text-primary text-25',
                        isOpen ? 'transform rotate-90' : ''
                    )}
                />
            </button>
            <div
                style={{ display: 'none' }}
                ref={contentRef}
                id={`content-${editedId}`}
                aria-labelledby={`btn-${editedId}`}
                className="pt-6"
            >
                <span
                    className={classNames('text-base text-14 lg:text-18 leading-28')}
                    dangerouslySetInnerHTML={{ __html: styledAnswer }}
                />
            </div>
        </li>
    );
};

export default Question;
